import {
    type ChatMessage,
    SPROUT_AGENT,
    toMastraResourceId,
} from '@fruitful/llm-service-shared';
import { AnalyticsUserEvent } from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { StorageKeys } from '../../../common/constants';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { MastraChat } from '../../../llm-service/components/MastraChat';
import { SPROUT_AI_DISCLOSURE } from '../../../llm-service/components/shared-chat/ChatInput';
import { MastraChatShell } from '../../../llm-service/components/shared-chat/MastraChatShell';
import {
    SPROUT_DEFAULT_SUGGESTIONS,
    SPROUT_INTRO_COPY,
} from '../../../llm-service/components/shared-chat/SproutEmptyState';
import { SproutOptionsButton } from '../../../llm-service/components/shared-chat/SproutOptionsButton';
import { useSproutStreamOptions } from '../../../llm-service/hooks/useSproutStreamOptions';
import { track } from '../../../logger/Logger';

const ChatPage = () => {
    const user = useAuthedUser();
    const { trackViewOnce } = useViewEvents();
    const hasLoggedNewSessionRef = useRef(false);
    const [isThreadListOpen, setIsThreadListOpen] = useState(false);
    const [threadId, setThreadId] = useState<string>();
    const {
        modelId,
        reasoningEffort,
        setModelId,
        setReasoningEffort,
        agent,
        setAgentKey,
    } = useSproutStreamOptions(threadId);
    const { value: enableSproutChat } = useFeatureGate(
        FeatureFlags.ENABLE_SPROUT_CHAT,
    );

    const { initialMessage } = useLocalSearchParams<{
        initialMessage?: string;
    }>();
    const greeting = user.firstName == null ? 'Hi' : `Hi ${user.firstName}`;
    // Set the user stytchId as the Mastra resourceId. In mastra / llm-service, the resourceId globally scopes resource access. We use user.stytchId over user.id so that we can avoid an api call to backend when performing auth server-side.
    const resourceId = toMastraResourceId(agent.id, user.stytchId);
    const persistThreadStorageKey =
        agent.id === SPROUT_AGENT.id
            ? StorageKeys.SPROUT_THREAD_ID
            : `${StorageKeys.SPROUT_THREAD_ID}:${agent.id}`;

    const initialMessages = useMemo<ChatMessage[] | undefined>(() => {
        if (initialMessage == null) {
            return undefined;
        }

        return [
            {
                id: `initial-${Date.now()}`,
                role: 'user',
                content: [
                    {
                        type: 'text',
                        text: initialMessage,
                    },
                ],
                createdAt: new Date(),
            },
        ];
    }, [initialMessage]);

    useEffect(() => {
        if (!enableSproutChat || hasLoggedNewSessionRef.current) {
            return;
        }

        hasLoggedNewSessionRef.current = true;
        trackViewOnce('FruitfulAIChat');
        track(AnalyticsUserEvent.FRUITFUL_AI_CHAT_NEW_SESSION, {
            resourceId,
            initialMessage: initialMessage ?? '',
        });
    }, [resourceId, initialMessage]);

    if (!enableSproutChat) {
        return <Redirect href="/" />;
    }

    const handleClose = () => {
        track(AnalyticsUserEvent.BACK);
        if (router.canGoBack()) {
            router.back();
        } else {
            router.navigate('/home');
        }
    };

    return (
        <MastraChatShell
            onClose={handleClose}
            onMenuPress={() => {
                setIsThreadListOpen(true);
            }}
            headerAccessory={
                <SproutOptionsButton
                    modelId={modelId}
                    reasoningEffort={reasoningEffort}
                    agentKey={agent.agentKey}
                    onModelIdChange={setModelId}
                    onReasoningEffortChange={setReasoningEffort}
                    onAgentKeyChange={setAgentKey}
                />
            }
        >
            <MastraChat
                key={agent.id}
                agent={agent}
                resourceId={resourceId}
                persistThreadStorageKey={persistThreadStorageKey}
                restoreThreadMessages
                initialMessages={initialMessages}
                greeting={greeting}
                introCopy={SPROUT_INTRO_COPY}
                suggestions={[...SPROUT_DEFAULT_SUGGESTIONS]}
                aiDisclosure={SPROUT_AI_DISCLOSURE}
                isThreadListOpen={isThreadListOpen}
                onThreadListOpenChange={setIsThreadListOpen}
                onThreadIdChange={setThreadId}
                sproutStreamModelId={modelId}
                sproutStreamReasoningEffort={reasoningEffort}
                membershipId={user.membership?.id}
            />
        </MastraChatShell>
    );
};

export default ChatPage;
